import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppShell, PageSection } from '@/components/app/AppShell';
import { forecastsApi, ComputeResult, CompareResult, RollForwardResult } from '@/lib/forecasts';

const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
const pct = (p: number | null) => (p == null ? '—' : `${(p * 100).toFixed(1)}%`);

export function PlanningPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();

  const { data: forecasts = [] } = useQuery({
    queryKey: ['forecasts', wsId],
    queryFn: () => forecastsApi.list(wsId!),
    enabled: !!wsId,
  });
  const { data: scenarios = [], refetch: refetchScenarios } = useQuery({
    queryKey: ['scenarios', wsId],
    queryFn: () => forecastsApi.scenarios(wsId!),
    enabled: !!wsId,
  });

  return (
    <AppShell title={t('planning.title')} subtitle={t('planning.subtitle')}>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('planning.title')}</h1>
          <p className="mt-1 text-ink-600">{t('planning.subtitle')}</p>
        </div>

        <PageSection eyebrow={t('planning.scenarios_eyebrow')} title={t('planning.scenarios_title')}>
          <div className="rounded-md border border-border bg-white overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 px-4 font-medium">{t('planning.col_scenario')}</th>
                  <th className="py-2 px-4 font-medium text-right">{t('planning.col_rows')}</th>
                  <th className="py-2 px-4 font-medium">{t('planning.col_span')}</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.length === 0 ? (
                  <tr><td colSpan={3} className="py-4 px-4 text-muted-foreground">{t('planning.no_scenarios')}</td></tr>
                ) : (
                  scenarios.map((s) => (
                    <tr key={s.scenario} className="border-b border-border/60">
                      <td className="py-2 px-4 num text-foreground">{s.scenario}</td>
                      <td className="py-2 px-4 num text-right tabular-nums">{num(s.rows)}</td>
                      <td className="py-2 px-4 num text-muted-foreground">{s.periodStart} → {s.periodEnd}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </PageSection>

        <ComputePanel wsId={wsId!} forecasts={forecasts} scenarios={scenarios} t={t} onComputed={() => refetchScenarios()} />
        <ComparePanel wsId={wsId!} scenarios={scenarios} t={t} />
      </main>
    </AppShell>
  );
}

function ComputePanel({
  wsId, forecasts, scenarios, t, onComputed,
}: {
  wsId: string;
  forecasts: { id: string; scenarioLabel: string }[];
  scenarios: { scenario: string }[];
  t: (k: string, o?: Record<string, unknown>) => string;
  onComputed: () => void;
}) {
  // Prefill the forecast when arriving from the Forecast page's "Plan/compute"
  // deep-link (?forecast=<id>), so the user doesn't have to re-select it.
  const [searchParams] = useSearchParams();
  const [forecastId, setForecastId] = useState(searchParams.get('forecast') ?? '');
  const [horizonStart, setHorizonStart] = useState('2026-04-01');
  const [horizonEnd, setHorizonEnd] = useState('2027-03-01');
  const [baseScenario, setBaseScenario] = useState('actual');
  const [throughPeriod, setThroughPeriod] = useState('');

  const compute = useMutation({
    mutationFn: () => forecastsApi.compute(forecastId, { horizonStart, horizonEnd, baseScenario }),
    onSuccess: onComputed,
  });
  const roll = useMutation({
    mutationFn: () => forecastsApi.rollForward(forecastId, { throughPeriod }),
    onSuccess: onComputed,
  });

  return (
    <PageSection eyebrow={t('planning.compute_eyebrow')} title={t('planning.compute_title')}>
      <div className="rounded-md border border-border bg-white p-5 space-y-4">
        {forecasts.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">{t('planning.no_forecasts')}</p>
            <Link
              to={`/app/workspace/${wsId}/forecast`}
              className="mt-3 inline-flex items-center px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition"
            >
              {t('planning.create_forecast')}
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t('planning.forecast')}>
                <select value={forecastId} onChange={(e) => setForecastId(e.target.value)} className={inputCls}>
                  <option value="">{t('planning.select_forecast')}</option>
                  {forecasts.map((f) => <option key={f.id} value={f.id}>{f.scenarioLabel}</option>)}
                </select>
              </Field>
              <Field label={t('planning.base_scenario')}>
                <select value={baseScenario} onChange={(e) => setBaseScenario(e.target.value)} className={inputCls}>
                  {['actual', ...scenarios.map((s) => s.scenario).filter((s) => s !== 'actual')].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('planning.horizon_start')}>
                <input type="date" value={horizonStart} onChange={(e) => setHorizonStart(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t('planning.horizon_end')}>
                <input type="date" value={horizonEnd} onChange={(e) => setHorizonEnd(e.target.value)} className={inputCls} />
              </Field>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => compute.mutate()}
                disabled={!forecastId || compute.isPending}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                {compute.isPending ? t('planning.computing') : t('planning.compute_btn')}
              </button>
              {compute.isError && <span className="text-xs text-warn-700">{(compute.error as Error).message}</span>}
            </div>

            {compute.data && <ComputeSummary r={compute.data} t={t} />}

            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground mb-2">{t('planning.rollforward_hint')}</p>
              <div className="flex items-end gap-2">
                <Field label={t('planning.through_period')}>
                  <input type="date" value={throughPeriod} onChange={(e) => setThroughPeriod(e.target.value)} className={inputCls} />
                </Field>
                <button
                  onClick={() => roll.mutate()}
                  disabled={!forecastId || !throughPeriod || roll.isPending}
                  className="px-4 py-2 rounded-lg border border-border text-foreground text-sm font-semibold hover:bg-foreground/[0.04] disabled:opacity-60"
                >
                  {roll.isPending ? t('planning.rolling') : t('planning.rollforward_btn')}
                </button>
              </div>
              {roll.isError && <span className="text-xs text-warn-700">{(roll.error as Error).message}</span>}
              {roll.data && <RollSummary r={roll.data} t={t} />}
            </div>
          </>
        )}
      </div>
    </PageSection>
  );
}

function ComputeSummary({ r, t }: { r: ComputeResult; t: (k: string, o?: Record<string, unknown>) => string }) {
  return (
    <div className="rounded-md bg-ink-50 border border-border p-4">
      <p className="text-[12px] text-emerald-700 font-semibold">
        {t('planning.computed_summary', { rows: r.rowsWritten, accounts: r.accounts, periods: r.periods, scenario: r.scenario })}
      </p>
      <TotalsTable totals={r.totalsByPeriod} t={t} />
    </div>
  );
}

function RollSummary({ r, t }: { r: RollForwardResult; t: (k: string, o?: Record<string, unknown>) => string }) {
  return (
    <div className="mt-3 rounded-md bg-ink-50 border border-border p-4">
      <p className="text-[12px] text-emerald-700 font-semibold">
        {t('planning.rolled_summary', { locked: r.lockedPeriods, projected: r.projectedPeriods, through: r.lockedThrough })}
      </p>
      <TotalsTable totals={r.totalsByPeriod} t={t} />
    </div>
  );
}

function TotalsTable({ totals, t }: { totals: Record<string, number>; t: (k: string, o?: Record<string, unknown>) => string }) {
  const entries = Object.entries(totals).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return <p className="text-xs text-muted-foreground mt-2">{t('planning.no_output')}</p>;
  return (
    <div className="mt-2 overflow-x-auto">
      <table className="text-[12px]">
        <tbody>
          {entries.map(([period, total]) => (
            <tr key={period}>
              <td className="num pr-4 py-0.5 text-muted-foreground">{period}</td>
              <td className="num py-0.5 tabular-nums text-right text-foreground">{num(total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComparePanel({
  wsId, scenarios, t,
}: {
  wsId: string;
  scenarios: { scenario: string }[];
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const [a, setA] = useState('budget');
  const [b, setB] = useState('actual');
  const compare = useMutation({
    mutationFn: () => forecastsApi.compare(wsId, { scenarioA: a, scenarioB: b }),
  });

  return (
    <PageSection eyebrow={t('planning.compare_eyebrow')} title={t('planning.compare_title')}>
      <div className="rounded-md border border-border bg-white p-5 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <Field label={t('planning.scenario_a')}>
            <select value={a} onChange={(e) => setA(e.target.value)} className={inputCls}>
              {scenarios.map((s) => <option key={s.scenario} value={s.scenario}>{s.scenario}</option>)}
            </select>
          </Field>
          <Field label={t('planning.scenario_b')}>
            <select value={b} onChange={(e) => setB(e.target.value)} className={inputCls}>
              {scenarios.map((s) => <option key={s.scenario} value={s.scenario}>{s.scenario}</option>)}
            </select>
          </Field>
          <button
            onClick={() => compare.mutate()}
            disabled={compare.isPending}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            {compare.isPending ? t('planning.comparing') : t('planning.compare_btn')}
          </button>
        </div>
        {compare.isError && <span className="text-xs text-warn-700">{(compare.error as Error).message}</span>}
        {compare.data && <CompareTable r={compare.data} wsId={wsId} t={t} />}
      </div>
    </PageSection>
  );
}

function CompareTable({ r, wsId, t }: { r: CompareResult; wsId: string; t: (k: string, o?: Record<string, unknown>) => string }) {
  return (
    <div className="overflow-x-auto">
      <p className="text-[12px] text-muted-foreground mb-2">
        {t('planning.compare_totals', { a: num(r.totals.a), b: num(r.totals.b), delta: num(r.totals.delta) })}
      </p>
      <table className="min-w-full text-[13px]">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 pr-4 font-medium">{t('planning.col_account')}</th>
            <th className="py-2 px-3 font-medium text-right">{r.scenarioA}</th>
            <th className="py-2 px-3 font-medium text-right">{r.scenarioB}</th>
            <th className="py-2 px-3 font-medium text-right">{t('planning.col_delta')}</th>
            <th className="py-2 pl-3 font-medium text-right">%</th>
          </tr>
        </thead>
        <tbody>
          {r.rows.slice(0, 50).map((row) => (
            <tr key={row.accountCode} className="border-b border-border/50">
              <td className="py-1.5 pr-4 num">
                <Link to={`/app/workspace/${wsId}/account-search?q=${encodeURIComponent(row.accountCode)}`} className="text-foreground hover:text-emerald-700 hover:underline" title={t('account_search.title')}>
                  {row.accountCode}
                </Link>
              </td>
              <td className="py-1.5 px-3 num text-right tabular-nums text-muted-foreground">{num(row.a)}</td>
              <td className="py-1.5 px-3 num text-right tabular-nums text-muted-foreground">{num(row.b)}</td>
              <td className={'py-1.5 px-3 num text-right tabular-nums ' + (row.delta >= 0 ? 'text-emerald-700' : 'text-warn-700')}>{num(row.delta)}</td>
              <td className="py-1.5 pl-3 num text-right tabular-nums text-muted-foreground">{pct(row.pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
